"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import {
  composePageTitle,
  useDetailPageTitle,
} from "@/lib/hooks/useDocumentTitle";

/**
 * Route → human page title. Longest matching prefix wins, so `/deals/123` falls
 * back to "Deals" until (and unless) a detail page overrides the title with the
 * record's own name via `useDocumentTitle` (e.g. UniversalDetailPage sets
 * "Libby Lytle" for a contact). Keep this a shallow section map — per-record
 * titles come from the pages that own the data, not from here.
 */
const ROUTE_TITLES: ReadonlyArray<readonly [string, string]> = [
  ["/admin/ceo-dashboard", "CEO Dashboard"],
  ["/admin", "Admin"],
  ["/deals", "Deals"],
  ["/contacts/dashboard", "Client Dashboard"],
  ["/contacts", "Contacts"],
  ["/companies", "Companies"],
  ["/tickets", "Tickets"],
  ["/tasks", "Tasks"],
  ["/communications", "Communications"],
  ["/contracts", "Contracts"],
  ["/reports", "Reports"],
  ["/time_entries", "Time Entries"],
  ["/recurring-charges", "Recurring Charges"],
  ["/recurring-transactions", "Recurring Transactions"],
  ["/sequences", "Sequences"],
  ["/offerings", "Offerings"],
  ["/dashboard/allocate", "Resource Allocation"],
  ["/dashboard", "Dashboard"],
  ["/chat", "Meridian"],
  ["/mktg", "Marketing"],
  ["/settings", "Settings"],
  ["/profile", "Profile"],
];

/** Resolve the section title for a pathname (longest prefix match). */
export function resolveRouteTitle(pathname: string | null): string | null {
  if (!pathname) return null;
  if (pathname === "/") return "Home";
  let best: string | null = null;
  let bestLen = -1;
  for (const [prefix, title] of ROUTE_TITLES) {
    if (
      (pathname === prefix || pathname.startsWith(`${prefix}/`)) &&
      prefix.length > bestLen
    ) {
      best = title;
      bestLen = prefix.length;
    }
  }
  return best;
}

/**
 * Shell-mounted title driver — the SINGLE authority for `document.title`.
 *
 * Title = the active detail-page title (a loaded record's name, published via
 * usePublishDetailPageTitle) if any, else the pathname's section title. Because
 * Hub routes are client components, Next re-applies the route's *default*
 * metadata title ("WTC Hub") on some client navigations AFTER our effect runs —
 * a race that left e.g. /contacts stuck on bare brand. A MutationObserver on the
 * <title> node re-asserts our value whenever anything else changes it, so we win
 * deterministically regardless of when Next's metadata lands. Keeping this the
 * only writer avoids two effects fighting on detail routes.
 *
 * Renders nothing.
 */
export function PageTitleManager(): null {
  const pathname = usePathname();
  const detailTitle = useDetailPageTitle();
  const desired = composePageTitle(detailTitle ?? resolveRouteTitle(pathname));

  useEffect(() => {
    if (typeof document === "undefined") return;

    const apply = () => {
      if (document.title !== desired) document.title = desired;
    };
    apply();

    // Re-assert against Next's metadata (or any other writer) clobbering us.
    const titleEl = document.querySelector("title");
    if (!titleEl) return;
    const observer = new MutationObserver(apply);
    observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
    return () => observer.disconnect();
  }, [desired]);

  return null;
}
